// ============================================================
// SALES PAGE
// src/SalesPage.js
// Combined view: Work Orders (new system) + Daily Uploads
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";
import { makeExactMatcher } from "./HistoricalImport";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

// ── SHARED STYLES ─────────────────────────────────────────────
const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  statCard:   (c) => ({background:"#fff",borderRadius:10,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${c}`}),
  th:         (r) => ({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"11px 16px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5},
};

// ── DAILY UPLOAD MODAL ────────────────────────────────────────
// Moved here from HistoricalImport
function parseCSVLocal(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers:[], rows:[] };
  const delimiters = ["\t",",",";","|"];
  const delim = delimiters.find(d => (lines[0].split(d).length) > 2) || ",";
  const splitLine = (line) => {
    const result=[]; let cur=""; let inQ=false;
    for (let i=0;i<line.length;i++) {
      const ch=line[i];
      if (ch==='"') inQ=!inQ;
      else if (ch===delim&&!inQ) { result.push(cur.trim()); cur=""; }
      else cur+=ch;
    }
    result.push(cur.trim());
    return result;
  };
  const headers = splitLine(lines[0]).map(h=>h.replace(/^"|"$/g,"").trim());
  const rows = lines.slice(1).map(line=>{
    const vals = splitLine(line);
    const obj={};
    headers.forEach((h,i)=>{ obj[h]=(vals[i]||"").replace(/^"|"$/g,"").trim(); });
    return obj;
  }).filter(r=>Object.values(r).some(v=>v));
  return { headers, rows };
}

function parseDateLocal(str) {
  if (!str) return null;
  str = str.trim().split(" ")[0];
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(str)) {
    const [d,m,y] = str.split(/[/-]/);
    return `${y}-${m.padStart(2,"0")}-${d.padStart(2,"0")}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const months={jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12"};
  const parts = str.split(/[\s/-]/);
  if (parts.length===3 && isNaN(parts[1])) {
    const m = months[parts[1].toLowerCase().slice(0,3)];
    if (m) return `${parts[2]}-${m}-${parts[0].padStart(2,"0")}`;
  }
  return null;
}

// Exact name matching now comes from HistoricalImport's makeExactMatcher —
// the fuzzy matcher that used to live here is what silently merged
// near-names like "SHRAVANTH" / "SHRAVANTH ARTISAN" into the same customer.

function DailyUploadModal({ onClose, onSave, toast }) {
  const [step, setStep]         = useState(1);
  const [preview, setPreview]   = useState([]);
  const [result, setResult]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [customers, setCustomers] = useState([]);
const [previewFilter, setPreviewFilter] = useState("all");
  const fileRef = useRef(null);

  useEffect(() => {
    const loadAll = async () => {
      let all = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase.from("customers")
          .select("id,name,phone").eq("is_active",true)
          .range(from, from + 999);
        if (error||!data||data.length===0) break;
        all = [...all, ...data];
        if (data.length < 1000) break;
        from += 1000;
      }
      setCustomers(all);
    };
    loadAll();
  }, []);

  const parseFile = async (file) => {
    let text = "";
    if (file.name.endsWith(".xlsx")||file.name.endsWith(".xls")) {
      if (!window.XLSX) {
        await new Promise((res,rej)=>{
          const s=document.createElement("script");
          s.src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
          s.onload=res;s.onerror=rej;document.head.appendChild(s);
        });
      }
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(new Uint8Array(buf),{type:"array"});
      const ws = wb.Sheets[wb.SheetNames[0]];
      text = window.XLSX.utils.sheet_to_csv(ws);
    } else {
      text = await file.text();
    }

    const { headers, rows } = parseCSVLocal(text);
    const dateCol  = headers.find(h => /date/i.test(h)) || headers[0];
    const orderCol = headers.find(h => /order/i.test(h));
    const nameCol  = headers.find(h => /name|customer/i.test(h));
    const typeCol  = headers.find(h => /type|work/i.test(h));
    const amtCol   = headers.find(h => /amount|amt|total/i.test(h));
    const matchCustomer = makeExactMatcher(customers);

    const parsed = rows.map(row => {
      const rawDate  = dateCol  ? row[dateCol]  : "";
      const isoDate  = parseDateLocal(rawDate);
      const custName = nameCol  ? row[nameCol]  : "";
      const amount   = parseFloat((amtCol ? row[amtCol] : "").replace(/[,₹\s]/g,"")) || 0;
      const orderNo  = orderCol ? row[orderCol] : "";
      const workType = typeCol  ? row[typeCol]  : "";
      const customer = matchCustomer(custName);
      return {
        rawDate, isoDate, custName, amount, orderNo, workType,
        customer,
        matchedName: customer?.name||null,
        customerId:  customer?.id||null,
 valid: !!(isoDate && custName), // amount can be 0 for rework/tracking orders
        noMatch:     !customer && !!custName,
      };
    }).filter(r => r.rawDate || r.custName);

setPreview(parsed);
setPreviewFilter("all");
setStep(2);
  };

  const uploadRows = async () => {
    setSaving(true);
    let inserted=0, skipped=0, unmatched=0;
    const errors=[];
    const validRows = preview.filter(r => r.valid && r.customerId);

    try {
      // Bulk dedup check up front — one query per 200 order numbers instead
      // of one query PER ROW, which is what made multi-year files crawl.
      const orderNos = [...new Set(validRows.map(r=>r.orderNo).filter(Boolean))];
      const existingOrderNos = new Set();
      for (let i=0;i<orderNos.length;i+=200) {
        const chunk = orderNos.slice(i,i+200);
        const { data, error } = await supabase.from("historical_transactions")
          .select("order_no").eq("txn_type","sale").in("order_no",chunk);
        if (error) throw error;
        (data||[]).forEach(d=>existingOrderNos.add(d.order_no));
      }

      const toInsert = [];
      validRows.forEach(row => {
        if (row.orderNo && existingOrderNos.has(row.orderNo)) { skipped++; return; }
        toInsert.push({
          customer_id:   row.customerId,
          customer_name: row.custName,
          txn_date:      row.isoDate,
          txn_type:      "sale",
          amount:        row.amount,
          reference:     row.orderNo||null,
          work_type:     row.workType||null,
          order_no:      row.orderNo||null,
          source:        "daily_upload",
        });
      });

      // Batched inserts — 500 rows per request instead of one per row. A
      // multi-row insert is one atomic statement, so if a chunk fails (one
      // bad row in it) that chunk is retried one row at a time instead of
      // losing the other ~499 good rows in it, then the next chunk continues.
      for (let i=0;i<toInsert.length;i+=500) {
        const batch = toInsert.slice(i,i+500);
        const { error } = await supabase.from("historical_transactions").insert(batch);
        if (!error) { inserted += batch.length; continue; }
        for (const row of batch) {
          const { error: rowErr } = await supabase.from("historical_transactions").insert(row);
          if (rowErr) errors.push(`${row.customer_name} (${row.txn_date}): ${rowErr.message}`);
          else inserted++;
        }
      }
    } catch(e) { errors.push(e.message); }

    unmatched = preview.filter(r => r.valid && !r.customerId).length;
    setSaving(false);
    setResult({ inserted, skipped, unmatched, errors,
      unmatchedRows: preview.filter(r => r.valid && !r.customerId) });
    setStep(3);
  };

  const validCount     = preview.filter(r=>r.valid&&r.customerId).length;
  const unmatchedCount = preview.filter(r=>r.valid&&!r.customerId).length;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:820,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Daily sales upload</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>
              {["","Upload file","Preview & confirm","Done"][step]}
            </div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>

        <div style={{padding:"20px 28px 28px"}}>
          {step===1&&(
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--info-l)",borderRadius:8,fontSize:13,color:"var(--info)"}}>
                Expected columns: <strong>Date · Name · Amount · Order No.</strong> (Work Type is optional)
                <div style={{marginTop:4,fontSize:12}}>Column order doesn't matter — any file with these columns is accepted. Rows with a duplicate order number are automatically skipped.</div>
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>Click to select file or drag and drop</div>
                <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>CSV, TSV or Excel (.xlsx)</div>
                <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" style={{display:"none"}}
                  onChange={e=>parseFile(e.target.files[0])}/>
              </div>
              <div style={{marginTop:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                <strong>Either format works, e.g.:</strong><br/>
                Date · Order_No · Name · Work Type · Amount<br/>
                20/04/2026 &nbsp;|&nbsp; O-AD-6042 &nbsp;|&nbsp; TRIDENT STUDIOS LLP &nbsp;|&nbsp; PHOTO BOOK &nbsp;|&nbsp; 1674
                <br/><br/>
                Date · Name · Amount · Order Number<br/>
                2026-08-15 &nbsp;|&nbsp; TPF VLR &nbsp;|&nbsp; 800 &nbsp;|&nbsp; DS-A-56670/DW
              </div>
            </div>
          )}

          {step===2&&(
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Total rows",      value:preview.length,   color:"var(--info)"},
                  {label:"Ready to import", value:validCount,       color:"var(--accent)"},
                  {label:"No customer match",value:unmatchedCount,  color:"var(--warn)"},
                  {label:"Invalid rows",    value:preview.filter(r=>!r.valid).length, color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>

{(unmatchedCount>0||preview.filter(r=>!r.valid).length>0)&&(
  <div style={{marginBottom:12,display:"flex",gap:8,flexWrap:"wrap"}}>
    {unmatchedCount>0&&(
      <button onClick={()=>setPreviewFilter(previewFilter==="nomatch"?"all":"nomatch")}
        style={{padding:"8px 14px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"var(--mono)",fontWeight:500,
          border:`1px solid var(--warn)`,
          background:previewFilter==="nomatch"?"var(--warn)":"var(--warn-l)",
          color:previewFilter==="nomatch"?"#fff":"var(--warn)"}}>
        ⚠ {unmatchedCount} no customer match {previewFilter==="nomatch"?"— showing":"— click to show"}
      </button>
    )}
    {preview.filter(r=>!r.valid).length>0&&(
      <button onClick={()=>setPreviewFilter(previewFilter==="invalid"?"all":"invalid")}
        style={{padding:"8px 14px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"var(--mono)",fontWeight:500,
          border:`1px solid var(--danger)`,
          background:previewFilter==="invalid"?"var(--danger)":"var(--danger-l)",
          color:previewFilter==="invalid"?"#fff":"var(--danger)"}}>
✕ {preview.filter(r=>!r.valid).length} invalid rows (missing date or customer) {previewFilter==="invalid"?"— showing":"— click to show"}
      </button>
    )}
    {previewFilter!=="all"&&(
      <button onClick={()=>setPreviewFilter("all")}
        style={{padding:"8px 14px",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"var(--mono)",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"}}>
        Show all rows
      </button>
    )}
  </div>
)}

              <div style={{maxHeight:400,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th>
                      <th style={S.th()}>Order no.</th>
                      <th style={S.th()}>Customer (file)</th>
                      <th style={S.th()}>Matched to</th>
                      <th style={S.th()}>Work type</th>
                      <th style={S.th(true)}>Amount</th>
                      <th style={S.th()}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(previewFilter==="all" ? preview :
  previewFilter==="nomatch" ? preview.filter(r=>r.noMatch) :
  previewFilter==="invalid" ? preview.filter(r=>!r.valid) : preview
).slice(0,200).map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)",background:!r.valid?"var(--paper)":r.noMatch?"#fff8f0":"#fff"}}>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{r.isoDate||r.rawDate}</td>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink3)"}}>{r.orderNo||"—"}</td>
                        <td style={{...S.td,fontWeight:500,fontSize:12}}>{r.custName||"—"}</td>
                        <td style={{...S.td,fontSize:12}}>
                          {r.matchedName
                            ? <span style={{color:"var(--accent)",fontWeight:500}}>{r.matchedName}</span>
                            : <span style={{color:"var(--warn)"}}>⚠ No match</span>}
                        </td>
                        <td style={{...S.td,fontSize:12,color:"var(--ink3)"}}>{r.workType||"—"}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500}}>{r.amount>0?fmt(r.amount):"—"}</td>
                        <td style={S.td}>
                          {!r.valid
                            ? <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,background:"var(--paper2)",color:"var(--ink3)"}}>Skip</span>
                            : r.noMatch
                              ? <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,background:"var(--warn-l)",color:"var(--warn)"}}>No match</span>
                              : <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,background:"var(--accent-l)",color:"var(--accent)"}}>✓ Ready</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
{(()=>{
  const shown = previewFilter==="all"?preview:previewFilter==="nomatch"?preview.filter(r=>r.noMatch):preview.filter(r=>!r.valid);
  return shown.length>200&&(
    <div style={{padding:"10px 14px",textAlign:"center",fontSize:12,color:"var(--ink3)",borderTop:"1px solid var(--paper2)"}}>
      Showing first 200 of {shown.length} {previewFilter==="all"?"rows":previewFilter==="nomatch"?"unmatched rows":"invalid rows"} — all {validCount} matched rows will be uploaded
    </div>
  );
})()}
              </div>

              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Re-upload</button>
                <button style={S.btnPrimary} onClick={uploadRows} disabled={saving||validCount===0}>
                  {saving?"Uploading…":`⬆ Import ${validCount} rows`}
                </button>
              </div>
            </div>
          )}

          {step===3&&result&&(
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>{result.errors.length===0?"✅":"⚠️"}</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Upload complete</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12,marginBottom:20,maxWidth:560,margin:"0 auto 20px"}}>
                {[
                  {label:"Inserted",     value:result.inserted,  color:"var(--accent)"},
                  {label:"Skipped (dup)",value:result.skipped,   color:"var(--info)"},
                  {label:"No match",     value:result.unmatched, color:"var(--warn)"},
                  {label:"Errors",       value:result.errors.length, color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {result.unmatchedRows?.length>0&&(
                <div style={{textAlign:"left",marginBottom:16,padding:"12px 16px",background:"var(--warn-l)",borderRadius:8,maxHeight:150,overflowY:"auto"}}>
                  <div style={{fontSize:12,fontWeight:500,color:"var(--warn)",marginBottom:6}}>Unmatched customers — add these and re-upload:</div>
                  {[...new Set(result.unmatchedRows.map(r=>r.custName))].map((n,i)=>(
                    <div key={i} style={{fontSize:12,color:"var(--warn)",marginBottom:2}}>· {n}</div>
                  ))}
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:16}}>
                <button style={S.btnPrimary} onClick={onSave}>✓ Done</button>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>Upload more</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── EDIT DAILY-UPLOAD ROW ────────────────────────────────────
// Lets a daily-uploaded transaction (from the old system) be corrected
// in place — e.g. an amount that turns out to be wrong in the source
// data — instead of having to delete and re-upload the whole file.
function EditUploadRowModal({ row, onClose, onSaved, toast }) {
  const [date, setDate]         = useState(row.txn_date||"");
  const [custQ, setCustQ]       = useState(row.customer_name||"");
  const [custId, setCustId]     = useState(row.customer_id||null);
  const [amount, setAmount]     = useState(row.amount??"");
  const [orderNo, setOrderNo]   = useState(row.order_no||"");
  const [workType, setWorkType] = useState(row.work_type||"");
  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    (async () => {
      let all = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase.from("customers")
          .select("id,name").eq("is_active",true).order("name")
          .range(from, from+999);
        if (error||!data||data.length===0) break;
        all = [...all, ...data];
        if (data.length<1000) break;
        from += 1000;
      }
      setCustomers(all);
    })();
  }, []);

  const filteredCusts = custQ
    ? matchItems(customers, custQ, c=>c.name, 50).list
    : [];

  const save = async () => {
    if (!date) return alert("Please set a date.");
    if (!custQ.trim()) return alert("Please enter a customer name.");
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt<0) return alert("Please enter a valid amount.");
    setSaving(true);
    try {
      // .select() so we can tell a genuine 0-row match (row deleted/changed
      // elsewhere since this modal opened) apart from success — Postgres
      // silently "succeeds" a no-op UPDATE, so without this the toast would
      // claim success while nothing actually changed.
      const { data, error } = await supabase.from("historical_transactions").update({
        txn_date: date,
        customer_id: custId,
        customer_name: custQ.trim(),
        amount: amt,
        order_no: orderNo||null,
        work_type: workType||null,
        updated_at: new Date().toISOString(),
      }).eq("id", row.id).select();
      if (error) throw error;
      if (!data || data.length===0) throw new Error("This transaction no longer exists — it may have been deleted or re-uploaded. Refresh the page and try again.");
      toast("Transaction updated!","success");
      onSaved();
    } catch(e) { alert("Error: "+e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:480}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={S.pageTitle}>Edit uploaded transaction</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          <div style={{marginBottom:14,padding:"9px 13px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
            From: {row.source==="daily_upload"?"Daily upload":"Manual entry"}{row.reference?` · ${row.reference}`:""}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
            <div>
              <label style={S.label}>Date *</label>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={S.label}>Amount (₹) *</label>
              <input type="number" min="0" step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} style={S.input}/>
            </div>
          </div>
          <div style={{marginBottom:16,position:"relative"}}>
            <label style={S.label}>Customer *</label>
            <input value={custQ} onChange={e=>{setCustQ(e.target.value);setCustId(null);setCustOpen(true);}}
              onFocus={()=>setCustOpen(true)} placeholder="Type to search customers…" style={S.input}/>
            {custOpen&&filteredCusts.length>0&&(
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:220,overflowY:"auto",marginTop:2}}>
                {filteredCusts.map(c=>(
                  <div key={c.id} style={{padding:"9px 13px",fontSize:13,cursor:"pointer",borderBottom:"1px solid var(--paper2)"}}
                    onMouseDown={()=>{setCustId(c.id);setCustQ(c.name);setCustOpen(false);}}>{c.name}</div>
                ))}
              </div>
            )}
            {!custId&&custQ&&<div style={{fontSize:11,color:"var(--warn)",marginTop:5}}>⚠ Not matched to an existing customer — pick one from the list, or save anyway to keep it as free text.</div>}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:8}}>
            <div>
              <label style={S.label}>Order no.</label>
              <input value={orderNo} onChange={e=>setOrderNo(e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={S.label}>Work type</label>
              <input value={workType} onChange={e=>setWorkType(e.target.value)} style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:20,paddingTop:18,borderTop:"1px solid var(--paper2)"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":"💾 Save changes"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SALES PAGE ────────────────────────────────────────────────
export function SalesPage({ toast }) {
  const [sales, setSales]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [sourceFilter, setSourceFilter] = useState("all"); // all | wo | upload
  const [fromDate, setFromDate]     = useState(""); // "" until the default (most recent) date is known
  const [toDate, setToDate]         = useState("");
  const [allDates, setAllDates]     = useState(false); // true = "Show all dates" was clicked
  const [uploadModal, setUploadModal] = useState(false);
  const [editUploadRow, setEditUploadRow] = useState(null);

  // Load runs scoped to [scopeFrom, scopeTo] (both null = unscoped/all dates)
  // instead of always paging through every row — with 5,000+ daily-upload
  // rows, an unconditional full load on every page visit made this crawl.
  const load = useCallback(async (scopeFrom, scopeTo) => {
    setLoading(true);
    const applyDateScope = (q, col) => scopeFrom&&scopeTo ? q.gte(col,scopeFrom).lte(col,scopeTo) : q;

    // Load WO sales — paginated
    let woAll = [];
    let from = 0;
    while (true) {
      let q = supabase.from("work_orders")
        .select("id,wo_number,order_date,customer_id,book_type,finish_type,total_sheets,status,customers(name)")
        .eq("is_deleted", false)
        .neq("status","cancelled");
      q = applyDateScope(q,"order_date");
      const { data, error } = await q.order("order_date", {ascending:false}).range(from, from+999);
      if (error||!data||data.length===0) break;
      woAll = [...woAll, ...data];
      if (data.length < 1000) break;
      from += 1000;
    }

    // Get WO totals from line items
    const woIds = woAll.map(w=>w.id);
    const woTotals = {};
    if (woIds.length > 0) {
      // Batch line item fetch
      for (let i=0; i<woIds.length; i+=500) {
        const batch = woIds.slice(i,i+500);
        const { data:li } = await supabase.from("wo_line_items")
          .select("work_order_id,quantity,unit_price")
          .in("work_order_id", batch);
        (li||[]).forEach(l => {
          woTotals[l.work_order_id] = (woTotals[l.work_order_id]||0) +
            (parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0);
        });
      }
    }

    // Build WO rows
    const woRows = woAll.map(w => ({
      id:         w.id,
      date:       w.order_date,
      ref:        w.wo_number,
      customer:   w.customers?.name||"—",
      workType:   [w.book_type, w.finish_type].filter(Boolean).join(" · "),
      amount:     woTotals[w.id]||0,
      source:     "wo",
      status:     w.status,
    }));
// Load direct work orders — paginated
let dwAll = [];
from = 0;
while (true) {
  let q = supabase.from("direct_work_orders")
    .select("id,dw_number,order_date,customer_id,notes,status,customers(name)")
    .eq("is_deleted", false)
    .neq("status","cancelled");
  q = applyDateScope(q,"order_date");
  const { data, error } = await q.order("order_date",{ascending:false}).range(from, from+999);
  if (error||!data||data.length===0) break;
  dwAll = [...dwAll,...data];
  if (data.length<1000) break;
  from += 1000;
}

// Get DW totals from line items
const dwIds = dwAll.map(w=>w.id);
const dwTotals = {};
if (dwIds.length > 0) {
  for (let i=0; i<dwIds.length; i+=500) {
    const batch = dwIds.slice(i,i+500);
    const {data:li} = await supabase.from("dw_line_items")
      .select("dw_order_id,quantity,unit_price")
      .in("dw_order_id", batch);
    (li||[]).forEach(l => {
      dwTotals[l.dw_order_id] = (dwTotals[l.dw_order_id]||0) +
        (parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0);
    });
  }
}

const dwRows = dwAll.map(w => ({
  id:       w.id,
  date:     w.order_date,
  ref:      w.dw_number,
  customer: w.customers?.name||"—",
  workType: w.notes||"—",
  amount:   dwTotals[w.id]||0,
  source:   "dw",
  status:   w.status,
}));
    // Load uploaded sales — paginated
    let uploadAll = [];
    from = 0;
    while (true) {
      let q = supabase.from("historical_transactions")
        .select("*")
        .eq("txn_type","sale")
        .eq("source","daily_upload");
      q = applyDateScope(q,"txn_date");
      const { data, error } = await q.order("txn_date",{ascending:false}).range(from, from+999);
      if (error||!data||data.length===0) break;
      uploadAll = [...uploadAll, ...data];
      if (data.length < 1000) break;
      from += 1000;
    }

    const uploadRows = uploadAll.map(r => ({
      id:       r.id,
      date:     r.txn_date,
      ref:      r.order_no||"—",
      customer: r.customer_name||"—",
      workType: r.work_type||"—",
      amount:   parseFloat(r.amount)||0,
      source:   "upload",
      status:   "uploaded",
    }));

    // Combine and sort by date descending
const combined = [...woRows, ...dwRows, ...uploadRows].sort((a,b) => a.date<b.date?1:-1);
    setSales(combined);
    setLoading(false);
  }, []);

  const [defaultDate, setDefaultDate] = useState("");

  // On first visit, find the most recent date across all 3 sources and
  // scope the load to just that day — with 5,000+ daily-upload rows an
  // unconditional full load on every visit made this crawl. Older data is
  // one date-range change (or "Show all dates") away, not gone.
  useEffect(() => {
    (async () => {
      const latestOf = async (table, col, extraFilters) => {
        let q = supabase.from(table).select(col);
        if (extraFilters) q = extraFilters(q);
        const { data } = await q.order(col,{ascending:false}).limit(1);
        return data && data[0] ? data[0][col] : null;
      };
      const [woLatest, dwLatest, upLatest] = await Promise.all([
        latestOf("work_orders","order_date", q=>q.eq("is_deleted",false).neq("status","cancelled")),
        latestOf("direct_work_orders","order_date", q=>q.eq("is_deleted",false).neq("status","cancelled")),
        latestOf("historical_transactions","txn_date", q=>q.eq("txn_type","sale").eq("source","daily_upload")),
      ]);
      const latest = [woLatest,dwLatest,upLatest].filter(Boolean).sort().pop() || new Date().toISOString().slice(0,10);
      setDefaultDate(latest);
      setFromDate(latest); setToDate(latest);
    })();
  }, []);

  useEffect(() => {
    if (allDates) { load(null,null); return; }
    if (fromDate && toDate) load(fromDate,toDate);
  }, [fromDate, toDate, allDates, load]);

  const showAllDates = () => { setAllDates(true); setFromDate(""); setToDate(""); };
  const setFromDateScoped = (v) => { setAllDates(false); setFromDate(v); };
  const setToDateScoped   = (v) => { setAllDates(false); setToDate(v); };
  const resetToDefault = () => {
    setSearch(""); setSourceFilter("all"); setAllDates(false);
    setFromDate(defaultDate); setToDate(defaultDate);
  };

  const openEditUpload = async (id) => {
    const { data, error } = await supabase.from("historical_transactions").select("*").eq("id",id).single();
    if (error) { toast("Couldn't load that transaction: "+error.message,"error"); return; }
    setEditUploadRow(data);
  };

  const filtered = sales.filter(s => {
    const q = search.toLowerCase();
    return (
      (s.customer.toLowerCase().includes(q) || s.ref.toLowerCase().includes(q)) &&
      (sourceFilter==="all" || s.source===sourceFilter)
    );
  });

  // Pagination — rendering everything in one table got heavy once a date
  // range or "Show all dates" pulled in hundreds/thousands of rows.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, sourceFilter, sales]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE);

const totalWO      = filtered.filter(s=>s.source==="wo").reduce((t,s)=>t+s.amount,0);
const totalDW      = filtered.filter(s=>s.source==="dw").reduce((t,s)=>t+s.amount,0);
const totalUpload  = filtered.filter(s=>s.source==="upload").reduce((t,s)=>t+s.amount,0);
const totalAll     = totalWO + totalDW + totalUpload;
const countWO      = filtered.filter(s=>s.source==="wo").length;
const countDW      = filtered.filter(s=>s.source==="dw").length;
const countUpload  = filtered.filter(s=>s.source==="upload").length;
  const downloadCSV = () => {
    const headers = ["Date","Reference","Customer","Work Type","Source","Amount"];
    const rows = filtered.map(s=>[
      fmtDMY(s.date), s.ref, s.customer, s.workType,
      s.source==="wo"?"Work Order":"Daily Upload",
      s.amount.toFixed(2),
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href=url; link.download=`sales-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const statusColors = {
    open:"badge-blue", in_progress:"badge-amber", completed:"badge-green",
    invoiced:"badge-purple", cancelled:"badge-red", uploaded:"badge-gray"
  };

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Sales</div>
<div style={S.subtitle}>
  Work orders ({countWO}) + Direct work ({countDW}) + Daily uploads ({countUpload}) · {filtered.length} total
  {allDates ? " · all dates" : fromDate ? (fromDate===toDate ? ` · ${fmtDMY(fromDate)}` : ` · ${fmtDMY(fromDate)} – ${fmtDMY(toDate)}`) : ""}
</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
          <button style={S.btnPrimary} onClick={()=>setUploadModal(true)}>⬆ Upload daily sales</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:16,marginBottom:28}}>
        {[
          {label:"Total sales",         value:fmt(totalAll),    color:"var(--info)"},
{label:"From work orders",   value:fmt(totalWO),     color:"var(--accent)"},
{label:"From direct work",   value:fmt(totalDW),     color:"var(--info)"},
{label:"From daily uploads", value:fmt(totalUpload), color:"#6a1b9a"},          {label:"Transactions",        value:filtered.length,  color:"var(--warn)"},
        ].map(s=>(
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={S.label}>Search customer / reference</label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
              <input placeholder="Customer name or order no…" value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,paddingLeft:34,background:"#fff"}}/>
            </div>
          </div>
          <div>
            <label style={S.label}>From date</label>
            <input type="date" value={fromDate} onChange={e=>setFromDateScoped(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={S.label}>To date</label>
            <input type="date" value={toDate} onChange={e=>setToDateScoped(e.target.value)} style={S.input}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {[
            {key:"all",    label:"All sources"},
            {key:"wo",     label:"Work orders only"},
{key:"dw",     label:"Direct work only"},
{key:"upload", label:"Daily uploads only"},
          ].map(f=>(
            <button key={f.key} onClick={()=>setSourceFilter(f.key)}
              style={{padding:"6px 12px",borderRadius:20,fontSize:12,cursor:"pointer",border:"1px solid var(--paper3)",
                background:sourceFilter===f.key?"var(--accent)":"#fff",
                color:sourceFilter===f.key?"#fff":"var(--ink2)",fontFamily:"var(--mono)"}}>
              {f.label}
            </button>
          ))}
          {!allDates&&(
            <button style={S.btnSm} onClick={showAllDates}>⬆ Show all dates</button>
          )}
          {(search||sourceFilter!=="all"||allDates||fromDate!==defaultDate||toDate!==defaultDate)&&(
            <button style={S.btnSm} onClick={resetToDefault}>✕ Reset to {defaultDate?fmtDMY(defaultDate):"latest"}</button>
          )}
          <span style={{fontSize:12,color:"var(--ink3)",marginLeft:"auto"}}>
            {filtered.length} row{filtered.length!==1?"s":""} · {fmt(totalAll)}
          </span>
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
              <th style={S.th()}>Date</th>
              <th style={S.th()}>Reference</th>
              <th style={S.th()}>Customer</th>
              <th style={S.th()}>Work type</th>
              <th style={S.th()}>Source</th>
              <th style={S.th(true)}>Amount</th>
              <th style={S.th()}>Status</th>
              <th style={S.th()}></th>
            </tr>
          </thead>
          <tbody>
            {loading&&(
              <tr><td colSpan={8} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>
                <span style={{display:"inline-block",width:16,height:16,border:"2px solid var(--paper3)",borderTopColor:"var(--accent)",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
                &nbsp; {allDates?"Loading all sales…":`Loading sales for ${fromDate?fmtDMY(fromDate):"…"}${fromDate!==toDate?` – ${toDate?fmtDMY(toDate):"…"}`:""}`}
              </td></tr>
            )}
            {!loading&&filtered.length===0&&(
              <tr><td colSpan={8}><div style={{textAlign:"center",padding:"60px 20px",color:"var(--ink3)"}}>
                <div style={{fontSize:37,marginBottom:12,opacity:0.5}}>📊</div>
                <p style={{fontSize:14}}>No sales found.</p>
              </div></td></tr>
            )}
            {!loading&&pageRows.map((s,i)=>(
              <tr key={s.id||i} style={{borderBottom:"1px solid var(--paper2)",
                background:s.source==="upload"?"#fafafa":"#fff"}}>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink2)"}}>{fmtDMY(s.date)}</td>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>
                  <span style={{background:"var(--paper2)",padding:"2px 7px",borderRadius:6,fontSize:12}}>{s.ref}</span>
                </td>
                <td style={{...S.td,fontWeight:500}}>{s.customer}</td>
                <td style={{...S.td,fontSize:12,color:"var(--ink3)"}}>{s.workType||"—"}</td>
                <td style={S.td}>
<span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,
  background:s.source==="wo"?"var(--accent-l)":s.source==="dw"?"var(--info-l)":"#f3e8ff",
  color:s.source==="wo"?"var(--accent)":s.source==="dw"?"var(--info)":"#6a1b9a"}}>
  {s.source==="wo"?"Work Order":s.source==="dw"?"Direct Work":"Daily Upload"}
</span>
                </td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,fontSize:14}}>
                  {s.amount>0?fmt(s.amount):"—"}
                </td>
                <td style={S.td}>
                  <span className={`badge ${statusColors[s.status]||"badge-gray"}`}>
                    {s.status?.replace("_"," ")||"—"}
                  </span>
                </td>
                <td style={S.td}>
                  {s.source==="upload"&&<button style={S.btnSm} onClick={()=>openEditUpload(s.id)}>✎ Edit</button>}
                </td>
              </tr>
            ))}
          </tbody>
          {!loading&&filtered.length>0&&(
            <tfoot>
              <tr style={{background:"var(--paper2)",borderTop:"2px solid var(--paper3)"}}>
                <td colSpan={5} style={{...S.td,fontWeight:500,color:"var(--ink3)"}}>
TOTAL · {countWO} work orders + {countDW} direct work + {countUpload} uploads
                </td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,fontSize:14,color:"var(--info)"}}>
                  {fmt(totalAll)}
                </td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {!loading&&filtered.length>PAGE_SIZE&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"0 4px"}}>
          <span style={{fontSize:12,color:"var(--ink3)"}}>
            Showing {(currentPage-1)*PAGE_SIZE+1}–{Math.min(currentPage*PAGE_SIZE,filtered.length)} of {filtered.length}
          </span>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button style={{...S.btnSm,opacity:currentPage<=1?0.4:1,cursor:currentPage<=1?"default":"pointer"}} disabled={currentPage<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>← Prev</button>
            <span style={{fontSize:12,color:"var(--ink3)",fontFamily:"var(--mono)"}}>Page {currentPage} of {totalPages}</span>
            <button style={{...S.btnSm,opacity:currentPage>=totalPages?0.4:1,cursor:currentPage>=totalPages?"default":"pointer"}} disabled={currentPage>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>Next →</button>
          </div>
        </div>
      )}

      {uploadModal&&(
        <DailyUploadModal
          onClose={()=>setUploadModal(false)}
          onSave={()=>{ setUploadModal(false); load(allDates?null:fromDate, allDates?null:toDate); toast("Sales uploaded!","success"); }}
          toast={toast}
        />
      )}

      {editUploadRow&&(
        <EditUploadRowModal
          row={editUploadRow}
          onClose={()=>setEditUploadRow(null)}
          onSaved={()=>{ setEditUploadRow(null); load(allDates?null:fromDate, allDates?null:toDate); }}
          toast={toast}
        />
      )}
    </div>
  );
}
